import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useStore } from '../store/useStore';
import {
  getFolders, createFolder, updateFolder, deleteFolder,
  getFiles, uploadFile, updateFile, deleteFile,
  getFileDownloadUrl,
} from '../utils/api';
import FileViewer from '../components/FileViewer';
import styles from './ProjectFiles.module.css';

// Finder-style Files browser. Left: folder tree. Right: contents of the
// selected folder (subfolders + files). Admin/PM can create/rename/delete
// folders, upload files, and edit file metadata. Field/readonly users see
// the same tree and file list but no write buttons — downloads still work.

export default function ProjectFiles() {
  const { project, role } = useStore();
  const canEdit = role === 'admin' || role === 'pm';

  const [folders, setFolders]           = useState([]);
  const [files,   setFiles]             = useState([]);
  // null = project root, otherwise the folder object
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [expanded, setExpanded]         = useState(() => new Set());
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingFiles,   setLoadingFiles]   = useState(false);

  // Modal state
  const [newFolderParent, setNewFolderParent] = useState(undefined); // undefined = closed; null = root; id = under
  const [renamingFolder,  setRenamingFolder]  = useState(null);
  const [editingFile,     setEditingFile]     = useState(null);
  const [uploadTarget,    setUploadTarget]    = useState(null); // { folderId, files? } when the modal is open
  const [viewingFile,     setViewingFile]     = useState(null); // file row when in-app viewer is open

  // ── Page-level drag-drop ──
  // Drop files anywhere on the right pane to upload them to the currently
  // selected folder. Browser fires dragenter/leave repeatedly as you cross
  // child elements, so we use a counter to stabilise the overlay state.
  const [draggingFile, setDraggingFile] = useState(false);
  const dragDepth = useRef(0);

  function onPageDragEnter(e) {
    if (!canEdit) return;
    // Ignore drags that aren't carrying files (e.g. text selection, internal
    // browser drags). dataTransfer.types is the safe pre-drop snoop API.
    const types = e.dataTransfer?.types || [];
    const hasFiles = Array.from(types).includes('Files');
    if (!hasFiles) return;
    e.preventDefault();
    dragDepth.current += 1;
    if (dragDepth.current === 1) setDraggingFile(true);
  }
  function onPageDragOver(e) {
    if (!canEdit) return;
    if (draggingFile) e.preventDefault(); // required to allow drop
  }
  function onPageDragLeave() {
    if (!canEdit) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDraggingFile(false);
  }
  function onPageDrop(e) {
    if (!canEdit) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDraggingFile(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (!dropped.length) return;
    setUploadTarget({ folderId: selectedFolderId, files: dropped });
  }

  // ── Load folder tree for this project ──
  async function refreshFolders() {
    if (!project) return;
    setLoadingFolders(true);
    try { setFolders(await getFolders(project.id)); }
    catch (err) { toast.error(err.message); }
    finally { setLoadingFolders(false); }
  }
  useEffect(() => { refreshFolders(); /* eslint-disable-next-line */ }, [project?.id]);

  // ── Load files whenever selected folder changes ──
  async function refreshFiles() {
    if (!project) return;
    setLoadingFiles(true);
    try {
      const params = { project_id: project.id };
      if (selectedFolderId) params.folder_id = selectedFolderId;
      else params.folder_id = 'root';
      setFiles(await getFiles(params));
    } catch (err) { toast.error(err.message); }
    finally { setLoadingFiles(false); }
  }
  useEffect(() => { refreshFiles(); /* eslint-disable-next-line */ }, [project?.id, selectedFolderId]);

  // ── Tree structure: build map from flat list ──
  // Children of a given parent_id (null = root level).
  const childrenOf = useMemo(() => {
    const map = new Map();
    for (const f of folders) {
      const key = f.parent_id || 'root';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    return map;
  }, [folders]);

  const folderById = useMemo(() => {
    const m = new Map();
    for (const f of folders) m.set(f.id, f);
    return m;
  }, [folders]);

  // Breadcrumb path from root → selected folder, for the contents-pane header.
  const breadcrumb = useMemo(() => {
    if (!selectedFolderId) return [];
    const out = [];
    let cur = folderById.get(selectedFolderId);
    while (cur) {
      out.unshift(cur);
      cur = cur.parent_id ? folderById.get(cur.parent_id) : null;
    }
    return out;
  }, [selectedFolderId, folderById]);

  // Subfolders of the currently-selected folder — shown at the top of the
  // contents pane alongside files (like Finder).
  const currentSubfolders = useMemo(() => {
    return childrenOf.get(selectedFolderId || 'root') || [];
  }, [childrenOf, selectedFolderId]);

  function toggleExpanded(id) {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // ── Folder actions ──
  async function handleCreateFolder(name, parentId) {
    try {
      await createFolder({ project_id: project.id, parent_id: parentId, name });
      toast.success(`Folder "${name}" created`);
      setNewFolderParent(undefined);
      await refreshFolders();
      if (parentId) setExpanded((s) => new Set(s).add(parentId));
    } catch (err) { toast.error(err.message); }
  }

  async function handleRenameFolder(id, name) {
    try {
      await updateFolder(id, { name });
      toast.success('Folder renamed');
      setRenamingFolder(null);
      await refreshFolders();
    } catch (err) { toast.error(err.message); }
  }

  async function handleDeleteFolder(folder) {
    const kids = childrenOf.get(folder.id)?.length || 0;
    const msg = kids
      ? `Delete "${folder.name}" and ALL ${kids} subfolder${kids === 1 ? '' : 's'} + any files inside? This cannot be undone.`
      : `Delete folder "${folder.name}"? Any files inside will also be deleted.`;
    if (!window.confirm(msg)) return;
    try {
      await deleteFolder(folder.id);
      toast.success(`Folder deleted`);
      if (selectedFolderId === folder.id) setSelectedFolderId(folder.parent_id);
      await Promise.all([refreshFolders(), refreshFiles()]);
    } catch (err) { toast.error(err.message); }
  }

  // ── File actions ──
  async function handleDeleteFile(file) {
    if (!window.confirm(`Delete "${file.name}"? This cannot be undone.`)) return;
    try {
      await deleteFile(file.id);
      toast.success('File deleted');
      await refreshFiles();
    } catch (err) { toast.error(err.message); }
  }

  // ── Render ──
  if (!project) return null;

  return (
    <div className={styles.page}>
      {/* ── Left: folder tree ── */}
      <aside className={styles.tree}>
        <div className={styles.treeHeader}>
          <span>FILES</span>
          {canEdit && (
            <button
              className={styles.iconBtn}
              title="New folder at root"
              onClick={() => setNewFolderParent(null)}
            >
              <NewFolderIcon />
            </button>
          )}
        </div>

        <button
          className={`${styles.rootRow} ${selectedFolderId === null ? styles.rowActive : ''}`}
          onClick={() => setSelectedFolderId(null)}
        >
          <FolderIcon />
          <span className={styles.rowName}>All Files</span>
        </button>

        <div className={styles.treeBody}>
          {loadingFolders && <div className={styles.empty}>Loading…</div>}
          {!loadingFolders && folders.length === 0 && (
            <div className={styles.empty}>
              {canEdit ? 'No folders yet — click the + above to create one.' : 'No folders yet.'}
            </div>
          )}
          {(childrenOf.get('root') || []).map((f) => (
            <FolderNode
              key={f.id}
              folder={f}
              depth={0}
              childrenOf={childrenOf}
              selectedId={selectedFolderId}
              onSelect={setSelectedFolderId}
              expanded={expanded}
              onToggle={toggleExpanded}
              canEdit={canEdit}
              onNewSubfolder={(parentId) => setNewFolderParent(parentId)}
              onRename={(folder) => setRenamingFolder(folder)}
              onDelete={handleDeleteFolder}
            />
          ))}
        </div>
      </aside>

      {/* ── Right: contents of selected folder ── */}
      <main
        className={styles.main}
        onDragEnter={onPageDragEnter}
        onDragOver={onPageDragOver}
        onDragLeave={onPageDragLeave}
        onDrop={onPageDrop}
      >
        {draggingFile && (
          <div className={styles.dropOverlay}>
            <div className={styles.dropOverlayInner}>
              <UploadIcon />
              <span className={styles.dropOverlayLabel}>
                Drop to upload to {selectedFolderId
                  ? folderById.get(selectedFolderId)?.name || 'this folder'
                  : 'All Files (root)'}
              </span>
            </div>
          </div>
        )}
        <header className={styles.mainHeader}>
          <div className={styles.crumbs}>
            <button
              className={`${styles.crumb} ${selectedFolderId === null ? styles.crumbActive : ''}`}
              onClick={() => setSelectedFolderId(null)}
            >
              All Files
            </button>
            {breadcrumb.map((b, i) => (
              <React.Fragment key={b.id}>
                <span className={styles.crumbSep}>/</span>
                <button
                  className={`${styles.crumb} ${i === breadcrumb.length - 1 ? styles.crumbActive : ''}`}
                  onClick={() => setSelectedFolderId(b.id)}
                >
                  {b.name}
                </button>
              </React.Fragment>
            ))}
          </div>

          <div className={styles.mainActions}>
            {canEdit && (
              <>
                <button
                  className={styles.btnSecondary}
                  onClick={() => setNewFolderParent(selectedFolderId)}
                >
                  <NewFolderIcon />
                  New Folder
                </button>
                <button
                  className={styles.btnPrimary}
                  onClick={() => setUploadTarget({ folderId: selectedFolderId })}
                >
                  <UploadIcon />
                  Upload
                </button>
              </>
            )}
          </div>
        </header>

        <div className={styles.listWrap}>
          {loadingFiles && <div className={styles.empty}>Loading files…</div>}

          {!loadingFiles && currentSubfolders.length === 0 && files.length === 0 && (
            <div className={styles.empty}>
              {canEdit
                ? 'Empty folder — click Upload to add files or New Folder to subdivide.'
                : 'Empty folder.'}
            </div>
          )}

          {currentSubfolders.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>Folders</div>
              <div className={styles.grid}>
                {currentSubfolders.map((f) => (
                  <button
                    key={f.id}
                    className={styles.folderCard}
                    onClick={() => setSelectedFolderId(f.id)}
                    onDoubleClick={() => setSelectedFolderId(f.id)}
                  >
                    <FolderIcon large />
                    <span className={styles.folderCardName}>{f.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {files.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>Files</div>
              <div className={styles.fileList}>
                <div className={styles.fileHeader}>
                  <span className={styles.fhName}>Name</span>
                  <span className={styles.fhMeta}>Size</span>
                  <span className={styles.fhMeta}>Uploaded</span>
                  <span className={styles.fhActions}></span>
                </div>
                {files.map((f) => (
                  <FileRow
                    key={f.id}
                    file={f}
                    canEdit={canEdit}
                    onView={() => setViewingFile(f)}
                    onEdit={() => setEditingFile(f)}
                    onDelete={() => handleDeleteFile(f)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ── Modals ── */}
      {newFolderParent !== undefined && (
        <NameModal
          title="New Folder"
          label="Folder name"
          initial=""
          onSubmit={(name) => handleCreateFolder(name, newFolderParent)}
          onClose={() => setNewFolderParent(undefined)}
        />
      )}

      {renamingFolder && (
        <NameModal
          title="Rename Folder"
          label="Folder name"
          initial={renamingFolder.name}
          onSubmit={(name) => handleRenameFolder(renamingFolder.id, name)}
          onClose={() => setRenamingFolder(null)}
        />
      )}

      {uploadTarget && (
        <UploadModal
          projectId={project.id}
          folderId={uploadTarget.folderId}
          folderLabel={
            uploadTarget.folderId
              ? folderById.get(uploadTarget.folderId)?.name || 'folder'
              : 'All Files (root)'
          }
          initialFiles={uploadTarget.files}
          onClose={() => setUploadTarget(null)}
          onUploaded={() => { setUploadTarget(null); refreshFiles(); }}
        />
      )}

      {editingFile && (
        <FileEditModal
          file={editingFile}
          folders={folders}
          onClose={() => setEditingFile(null)}
          onSaved={() => { setEditingFile(null); refreshFiles(); }}
        />
      )}

      {viewingFile && (
        <FileViewer
          file={viewingFile}
          onClose={() => setViewingFile(null)}
        />
      )}
    </div>
  );
}

// ── FolderNode: recursive tree row ──
function FolderNode({
  folder, depth, childrenOf, selectedId, onSelect,
  expanded, onToggle, canEdit, onNewSubfolder, onRename, onDelete,
}) {
  const kids = childrenOf.get(folder.id) || [];
  const isOpen = expanded.has(folder.id);
  const isSelected = selectedId === folder.id;

  return (
    <div className={styles.nodeWrap}>
      <div
        className={`${styles.node} ${isSelected ? styles.rowActive : ''}`}
        style={{ paddingLeft: 8 + depth * 14 }}
      >
        <button
          className={styles.twisty}
          onClick={() => onToggle(folder.id)}
          // Non-folders have no caret — but we keep the space for alignment
          // by showing a small muted dash when empty.
          aria-label={isOpen ? 'Collapse' : 'Expand'}
        >
          {kids.length > 0 ? (isOpen ? <ChevronDown /> : <ChevronRight />) : <span className={styles.twistyDash}>·</span>}
        </button>
        <button
          className={styles.nodeName}
          onClick={() => onSelect(folder.id)}
        >
          <FolderIcon />
          <span className={styles.rowName}>{folder.name}</span>
        </button>
        {canEdit && (
          <div className={styles.nodeActions}>
            <button
              className={styles.iconBtn}
              title="New subfolder"
              onClick={(e) => { e.stopPropagation(); onNewSubfolder(folder.id); }}
            ><NewFolderIcon /></button>
            <button
              className={styles.iconBtn}
              title="Rename"
              onClick={(e) => { e.stopPropagation(); onRename(folder); }}
            ><PencilIcon /></button>
            <button
              className={styles.iconBtn}
              title="Delete"
              onClick={(e) => { e.stopPropagation(); onDelete(folder); }}
            ><TrashIcon /></button>
          </div>
        )}
      </div>
      {isOpen && kids.map((k) => (
        <FolderNode
          key={k.id}
          folder={k}
          depth={depth + 1}
          childrenOf={childrenOf}
          selectedId={selectedId}
          onSelect={onSelect}
          expanded={expanded}
          onToggle={onToggle}
          canEdit={canEdit}
          onNewSubfolder={onNewSubfolder}
          onRename={onRename}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

// ── FileRow ──
function FileRow({ file, canEdit, onView, onEdit, onDelete }) {
  const date = file.created_at ? format(new Date(file.created_at), 'MMM d, yyyy') : '';
  const size = formatSize(file.size);
  const downloadUrl = getFileDownloadUrl(file.id, { download: true });

  return (
    <div className={styles.fileRow}>
      {/* Click the name to open the in-app viewer (PDF search, image,
          text). Download button on the right still hands the bytes
          straight to the OS — useful for unsupported types or anything
          the user wants offline. */}
      <button
        type="button"
        className={styles.fileNameCol}
        title={file.description || file.name}
        onClick={onView}
      >
        <FileIcon mime={file.mime_type} />
        <div className={styles.fileNameStack}>
          <span className={styles.fileName}>{file.name}</span>
          {file.description && <span className={styles.fileDesc}>{file.description}</span>}
        </div>
      </button>
      <span className={styles.fileMeta}>{size}</span>
      <span className={styles.fileMeta}>
        <span className={styles.metaDate}>{date}</span>
        {file.uploaded_by && <span className={styles.metaBy}> · {file.uploaded_by}</span>}
      </span>
      <div className={styles.fileActions}>
        <a
          href={downloadUrl}
          className={styles.iconBtn}
          title="Download"
          download={file.name}
        >
          <DownloadIcon />
        </a>
        {canEdit && (
          <>
            <button className={styles.iconBtn} title="Edit" onClick={onEdit}><PencilIcon /></button>
            <button className={styles.iconBtn} title="Delete" onClick={onDelete}><TrashIcon /></button>
          </>
        )}
      </div>
    </div>
  );
}

// ── NameModal: small shared prompt for create / rename ──
function NameModal({ title, label, initial, onSubmit, onClose }) {
  const [value, setValue] = useState(initial || '');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);
  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className={styles.modal}
        onSubmit={(e) => { e.preventDefault(); if (value.trim()) onSubmit(value.trim()); }}
      >
        <div className={styles.modalHeader}>
          <span>{title}</span>
          <button type="button" className={styles.closeBtn} onClick={onClose}>×</button>
        </div>
        <div className={styles.modalBody}>
          <label className={styles.modalLabel}>{label}</label>
          <input
            ref={inputRef}
            className={styles.modalInput}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnSecondary} onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={!value.trim()}
          >
            OK
          </button>
        </div>
      </form>
    </div>
  );
}

// ── UploadModal: multi-file + drag-drop + per-file progress ──
// `initialFiles` lets the page drop files directly into the modal (page-level
// drag-drop opens this modal pre-loaded). Description applies to every file
// in the batch — keep it focused; per-file edits happen later via FileEditModal.
function UploadModal({ projectId, folderId, folderLabel, onClose, onUploaded, initialFiles }) {
  const [files, setFiles] = useState(() => Array.from(initialFiles || []));
  const [description, setDescription] = useState('');
  // index → percent. Sparse — only entries that started uploading appear.
  const [progressByIdx, setProgressByIdx] = useState({});
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  function addFiles(incoming) {
    const arr = Array.from(incoming || []);
    if (!arr.length) return;
    // Dedupe by (name + size) so picking the same file twice doesn't double it.
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}`));
      const next = [...prev];
      for (const f of arr) {
        const key = `${f.name}|${f.size}`;
        if (!seen.has(key)) { next.push(f); seen.add(key); }
      }
      return next;
    });
  }
  function removeFile(idx) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!files.length) return;
    setUploading(true);
    let done = 0;
    const failed = [];
    // Sequential: keeps server / DB happy on slow links and avoids fighting
    // the upload bandwidth between concurrent files.
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        await uploadFile({
          project_id: projectId,
          folder_id:  folderId,
          description,
          file:       f,
          onProgress: (ev) => {
            if (ev.total) {
              const pct = Math.round((ev.loaded / ev.total) * 100);
              setProgressByIdx((p) => ({ ...p, [i]: pct }));
            }
          },
        });
        done++;
      } catch (err) {
        failed.push({ name: f.name, msg: err.message });
      }
    }
    if (done)         toast.success(`Uploaded ${done} file${done === 1 ? '' : 's'}`);
    for (const f of failed) toast.error(`${f.name}: ${f.msg}`);
    onUploaded();
  }

  function onDragOver(e) {
    e.preventDefault();
    if (!dragging) setDragging(true);
  }
  function onDragLeave(e) {
    // Only clear when leaving the dropzone itself, not its children.
    if (e.currentTarget === e.target) setDragging(false);
  }
  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer?.files);
  }

  const totalBytes = files.reduce((a, f) => a + (f.size || 0), 0);

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && !uploading && onClose()}>
      <form className={styles.modal} onSubmit={handleSubmit}>
        <div className={styles.modalHeader}>
          <span>Upload to {folderLabel}</span>
          <button type="button" className={styles.closeBtn} onClick={onClose} disabled={uploading}>×</button>
        </div>
        <div className={styles.modalBody}>
          <div
            className={`${styles.dropzone} ${dragging ? styles.dropzoneActive : ''} ${files.length ? styles.dropzoneCompact : ''}`}
            onClick={() => !uploading && inputRef.current?.click()}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              disabled={uploading}
            />
            <span className={styles.dzPrompt}>
              {files.length
                ? `Add more — drop here or click to browse`
                : `Drag & drop files here, or click to browse`}
            </span>
          </div>

          {files.length > 0 && (
            <div className={styles.fileQueue}>
              {files.map((f, i) => {
                const pct = progressByIdx[i];
                return (
                  <div key={`${f.name}-${i}`} className={styles.fileQueueRow}>
                    <span className={styles.fileQueueName} title={f.name}>{f.name}</span>
                    <span className={styles.fileQueueSize}>{formatSize(f.size)}</span>
                    {uploading ? (
                      <div className={styles.miniProgressTrack}>
                        <div className={styles.miniProgressFill} style={{ width: `${pct || 0}%` }} />
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={styles.iconBtn}
                        title="Remove"
                        onClick={() => removeFile(i)}
                      ><TrashIcon /></button>
                    )}
                  </div>
                );
              })}
              <div className={styles.fileQueueTotal}>
                {files.length} file{files.length === 1 ? '' : 's'} · {formatSize(totalBytes)}
              </div>
            </div>
          )}

          <label className={styles.modalLabel}>Description (optional, applied to all)</label>
          <textarea
            className={styles.modalTextarea}
            rows={2}
            placeholder="e.g. Daikin VRV cutsheets — spec'd 2026-04-10"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={uploading}
          />
        </div>
        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={uploading}>Cancel</button>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={!files.length || uploading}
          >
            {uploading
              ? `Uploading… ${Object.keys(progressByIdx).length}/${files.length}`
              : files.length <= 1 ? 'Upload' : `Upload ${files.length} files`}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── FileEditModal: rename / description / move ──
function FileEditModal({ file, folders, onClose, onSaved }) {
  const [name, setName] = useState(file.name);
  const [description, setDescription] = useState(file.description || '');
  const [folderId, setFolderId] = useState(file.folder_id || '');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await updateFile(file.id, {
        name,
        description,
        folder_id: folderId === '' ? null : folderId,
      });
      toast.success('File updated');
      onSaved();
    } catch (err) {
      toast.error(err.message);
      setSaving(false);
    }
  }

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <form className={styles.modal} onSubmit={handleSubmit}>
        <div className={styles.modalHeader}>
          <span>Edit file</span>
          <button type="button" className={styles.closeBtn} onClick={onClose}>×</button>
        </div>
        <div className={styles.modalBody}>
          <label className={styles.modalLabel}>Name</label>
          <input className={styles.modalInput} value={name} onChange={(e) => setName(e.target.value)} />
          <label className={styles.modalLabel}>Description</label>
          <textarea
            className={styles.modalTextarea}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <label className={styles.modalLabel}>Folder</label>
          <select
            className={styles.modalInput}
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
          >
            <option value="">All Files (root)</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{folderPath(f, folders)}</option>
            ))}
          </select>
        </div>
        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className={styles.btnPrimary} disabled={saving || !name.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Helpers
function folderPath(f, all) {
  const byId = new Map(all.map((x) => [x.id, x]));
  const parts = [];
  let cur = f;
  while (cur) {
    parts.unshift(cur.name);
    cur = cur.parent_id ? byId.get(cur.parent_id) : null;
  }
  return parts.join(' / ');
}

function formatSize(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// ── Icons ──
const FolderIcon = ({ large }) => (
  <svg width={large ? 28 : 14} height={large ? 28 : 14} viewBox="0 0 20 20" fill="none">
    <path d="M2 5.5a1.5 1.5 0 011.5-1.5h4l1.5 2h7.5A1.5 1.5 0 0118 7.5v7A1.5 1.5 0 0116.5 16h-13A1.5 1.5 0 012 14.5v-9z"
      fill="var(--accent-dim)" stroke="var(--accent)" strokeWidth="1.3"/>
  </svg>
);
const FileIcon = ({ mime }) => {
  // Tint PDFs, images, docs differently for quick recognition at list density.
  let tint = 'var(--text-dim)';
  if (mime?.startsWith('image/')) tint = 'var(--green)';
  else if (mime === 'application/pdf') tint = 'var(--red)';
  else if (mime?.includes('word')) tint = 'var(--blue)';
  else if (mime?.includes('sheet') || mime?.includes('excel')) tint = 'var(--green)';
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
      <path d="M5 2h7l4 4v11a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z"
        stroke={tint} strokeWidth="1.4" strokeLinejoin="round"/>
      <path d="M12 2v4h4" stroke={tint} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};
const NewFolderIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
    <path d="M2 5.5a1.5 1.5 0 011.5-1.5h4l1.5 2h7.5A1.5 1.5 0 0118 7.5v7A1.5 1.5 0 0116.5 16h-13A1.5 1.5 0 012 14.5v-9z"
      stroke="currentColor" strokeWidth="1.3"/>
    <path d="M10 9v4M8 11h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
const UploadIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M8 1v9M5 4l3-3 3 3M2 12v2a1 1 0 001 1h10a1 1 0 001-1v-2"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);
const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M8 1v9M5 7l3 3 3-3M2 12v2a1 1 0 001 1h10a1 1 0 001-1v-2"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const PencilIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M11 2l3 3-8 8H3v-3l8-8z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M6 4V3h4v1M5 4l.5 9h5L11 4"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ChevronDown = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
const ChevronRight = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M3.5 2L6.5 5L3.5 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
